import { useEffect, useState } from "react";
import styles from "./Emandate.module.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import MainHeader from "../../components/Header/MainHeader";
import ReversePenny from "./ReversePenny";
import Drawer from "@mui/material/Drawer";
import InfoMessage from "../../components/Messages/InfoMessage";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import { useDispatch, useSelector } from "react-redux";
import useResponse from "../../hooks/useResponse";
import { rozarPayMandate } from "../../lib/payments";
import { useTheme } from "@mui/material/styles";
import { googleEvent_LOJ151, googleEvent_LOJ153, googleEvent_LOJ212, googleEvent_LOJ229 } from "../../lib/google-analytics";
import { setStepperDisable } from "../../redux/slices/userSlice";
import { ORDER_ID_LOCAL_STORAGE_KEY } from "../../Constants/constant";
import { clevertap_sv_btmsld_emandate, clevertap_btmsld_emandate_man_continue_clk, clevertap_sv_penny_drop } from "../../lib/clevertap-events";

let pollingInterval;
let pollingTimeout;
let hasOpenedWindow = false;

export default function Emandate() {
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({ bottom: false });
  const [pennyDropRetry, setPennyDropRetry] = useState(false);
  const [isReverseFlow, setIsReverseFlow] = useState("");

  const dispatch = useDispatch();

  const theme = useTheme();
  const { accountId } = useSelector((state) => state.user);
  const { handleStep } = useResponse();
  const navigate = useNavigate();
  const location = useLocation();

  const { setEmandate, setEmandateV2, pennyDrop, getPennyFlowAPI } = new AfterOnboardAPI();

  // Handle retry flow
  useEffect(() => {
    if (pennyDropRetry) {
      console.log("Retrying penny drop...");
      handlePannyDropAPI();
      setPennyDropRetry(false);
    }
  }, [pennyDropRetry]);

  useEffect(() => {
    // Events
    googleEvent_LOJ151();
    clevertap_sv_btmsld_emandate();
  }, []);

  useEffect(() => {
    state.bottom && clevertap_sv_penny_drop();
  }, [state.bottom]);

  const fetchPennyFlow = async () => {
    try {
      const res = await getPennyFlowAPI();
      if (res?.statusCode === 200) {
        const { flow } = res?.data;
        console.log("Flow from getPennyFlowAPI:", flow);
        if (flow === "reverse") {
          setIsReverseFlow("reverse");
        }

        if (flow !== "reverse") {
          handlePannyDropAPI();
          setState({ ...state, bottom: true });
        }
      }
    } catch (error) {
      console.error("Error fetching bank details", error);
    }
  };

  const toggleDrawer = (anchor, open) => (event) => {
    if (event?.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
    setState((prev) => ({ ...prev, [anchor]: open }));
  };

  const handleEmandateV2 = async () => {
    setLoading(true);
    try {
      const res = await setEmandateV2(accountId);

      if (res?.statusCode === 200 && res?.message === "Success") {
        await rozarPayMandate(res?.data);
        googleEvent_LOJ229();
        localStorage.setItem(ORDER_ID_LOCAL_STORAGE_KEY, res?.data?.order_id || "");
      } else if (res?.statusCode === 200 && res?.message === "Already Emandate") {
        fetchPennyFlow();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        text: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }

    // Events
    googleEvent_LOJ153();
    clevertap_btmsld_emandate_man_continue_clk();
  };

  const handlePannyDropAPI = async () => {
    try {
      setLoading(true);

      const res = await pennyDrop(accountId);
      const url = res?.data?.metaData?.url;
      if (res) {
        if (res?.data?.pennyStatus === "created" && url) {
          setState({ ...state, bottom: true });
          pollingInterval = setInterval(() => {
            handlePannyDropAPI();
          }, 10000);

          pollingTimeout = setTimeout(() => {
            clearInterval(pollingInterval);
          }, 5 * 60 * 1000);

          if (url && !hasOpenedWindow) {
            hasOpenedWindow = true;
            window.open(url, "_blank");
          }
        } else if (res?.data?.pennyStatus === "completed" || res.data.pennyStatus === "failed") {
          clearInterval(pollingInterval);
          clearTimeout(pollingTimeout);
          handleStep(accountId);

          setTimeout(() => {
            setLoading(false);
            setState({ ...state, bottom: false });
            dispatch(setStepperDisable(false));
          }, 4000);
        } else {
          setPennyDropRetry(true);
          googleEvent_LOJ212();
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      setState({ ...state, bottom: false });
      setLoading(false);
      const message = error?.response?.data?.message;
      const status = error?.status;
      const pennyStatus = error?.response?.data?.data?.pennyStatus;
      const flow = error?.response?.data?.data?.metaData?.flow;
      Swal.fire({
        icon: "error",
        text: message,
        showConfirmButton: false,
        timer: 1500,
      });

      if ((status === 400 && pennyStatus === "rejected") || (status === 400 && pennyStatus === "invalid") || status === 422 || status === 500) {
        navigate("/loan-approval");
        clearInterval(pollingInterval);
        clearTimeout(pollingTimeout);
      } else if (status === 400 && pennyStatus === "incomplete") {
        if (flow === "reverse") {
          setIsReverseFlow("reverse");

          clearInterval(pollingInterval);
          clearTimeout(pollingTimeout);
        } else {
          navigate("/loan-approval");
          clearInterval(pollingInterval);
          clearTimeout(pollingTimeout);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Render Reverse Flow or Default UI */}
      {isReverseFlow === "reverse" ? (
        <ReversePenny handlePannyDropAPI={handlePannyDropAPI} />
      ) : (
        <div className="wrapper">
          <div className="vertical-column-layout">
            <div className="vertical-column-top">
              <MainHeader title="E-mandate" />
              <p className="label">Complete your E-mandate</p>
              <p className="sub-label mb-2">Please complete your e-mandate process for loan disbursal.</p>
              <div>
                <img src="/images/emandate.png" alt="E-mandate" />
              </div>
              <InfoMessage message="After completing e-mandate process come back to the application for further process." />
            </div>

            <div className="vertical-column-bottom">
              <button className="button" type="submit" onClick={handleEmandateV2}>
                Continue <i className="shine"></i>
              </button>

              <div className={styles.edit__bank}>
                <Link to="/bank-details" state={{ backScreen: "/emandate" }}>
                  Edit Bank Details
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Drawer */}
      <Drawer
        anchor="bottom"
        open={state["bottom"]}
        onClose={toggleDrawer("bottom", false)}
        className={styles.bottomDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            maxWidth: 480,
            width: "100%",
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            margin: "0 auto",
            left: "0",
            [theme.breakpoints.up("sm")]: {
              left: -15,
            },
          },
        }}
      >
        <div className={styles.mandateProcessModal}>
          {isReverseFlow !== "reverse" && <p className="mb-2">Just wait for a moment</p>}

          <div>
            <img src="/images/send-to-bank.svg" alt="Verifying" />
          </div>

          <h3 className={styles.h3Buttom}>
            {isReverseFlow === "reverse" ? (
              <>
                Please wait, we are verifying your <br /> bank account
              </>
            ) : (
              <>
                Sending ₹ 1 to your <br /> bank for verification
              </>
            )}
          </h3>
        </div>
      </Drawer>
    </>
  );
}
